<script>
import date from 'locutus/php/datetime/date'
import defaults from 'lodash/defaults'
import {
  fetchInstrument,
  createInstrument,
  updateInstrument,
  fetchInstrumentDivearns,
  fetchInstrumentDivearn,
  createInstrumentDivearn,
  deleteInstrumentDivearn,
} from '@apis/admin/finance'
import { fetchAssets } from '@apis/admin/asset'
import { fetchForms as fetchContractForms } from '@apis/admin/contract'
import LangItem from '@components/admin/lang-input'
import { mapState } from 'vuex'
import fromPairs from 'lodash/fromPairs'
import find from 'lodash/find'

export default {
  components: { LangItem },
  props: {
    instrument_seq: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      params: {},
      info: null,
      assets: [],
      contract_forms: [],
      instrument_divearns: [],
      divearn_params: null,
      loading: false,
      active_tab: 'info',
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
    c_execute_asset_info() {
      return find(this.assets, { seq: this.params.execute_asset_seq })
    },
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.loading = true
      Promise.all([
        this.loadInstrument(),
        this.loadAssets(),
        this.loadContractForms(),
      ])
        .then(() => this.loadInstrumentDivearns())
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadInstrument() {
      let { instrument_info } = await fetchInstrument(this.instrument_seq)
      this.params = instrument_info = defaults(instrument_info, {
        seq: null,
        contract_form_seq: null,
        execute_asset_seq: null,
        name: '',
        title: '',
        summary: '',
        instrument_type: 'DIVEARN',
        use_open_start: 'N',
        open_start: null,
        use_open_end: 'N',
        open_end: null,
        use_execute_term_min: 'N',
        execute_term_min: null,
        use_execute_term_max: 'N',
        execute_term_max: null,
        use_execute_term_step: 'N',
        execute_term_step: 1,
        execute_term_unit: 'DAY',
        use_execute_amount_min: 'N',
        execute_amount_min: null,
        use_execute_amount_max: 'N',
        execute_amount_max: null,
        execute_amount_step: 1,
        execute_start: null,
        execute_end: null,
        adjust_start: null,
        regdate: null,
      })

      instrument_info.open_start = instrument_info.open_start
        ? new Date(instrument_info.open_start)
        : null
      instrument_info.open_end = instrument_info.open_end
        ? new Date(instrument_info.open_end)
        : null
      instrument_info.execute_start = instrument_info.execute_start
        ? new Date(instrument_info.execute_start)
        : null
      instrument_info.execute_end = instrument_info.execute_end
        ? new Date(instrument_info.execute_end)
        : null
      instrument_info.adjust_start = instrument_info.adjust_start
        ? new Date(instrument_info.adjust_start)
        : null

      this.info = instrument_info
    },
    async loadAssets() {
      const { data } = await fetchAssets()

      this.assets = data
    },
    async loadContractForms() {
      const { data } = await fetchContractForms()

      this.contract_forms = data
    },
    async loadInstrumentDivearns() {
      if (!this.info) return []

      const { data } = await fetchInstrumentDivearns(this.info.seq)

      this.instrument_divearns = data
    },
    async loadInstrumentDivearn(document_seq) {
      const { data } = await fetchInstrumentDivearn(this.info.seq, document_seq)

      this.divearn_params = data
    },
    handleSubmit() {
      const createOrUpdate = this.params.seq
        ? updateInstrument
        : createInstrument
      const confirm_message = this.params.seq
        ? this.$t('admin.msg_save')
        : this.$t('admin.msg_enroll')
      const success_message = this.params.seq
        ? this.$t('admin.msg_instrument_modify')
        : this.$t('admin.msg_instrument_enroll')

      const params = { ...this.params }
      const date_attrs = [
        'open_start',
        'open_end',
        'execute_start',
        'execute_end',
        'adjust_start',
      ]
      for (const attr of date_attrs) {
        if (params[attr]) {
          params[attr] = new Date(params[attr]).getTime()
        }
      }

      this.loading = true
      this.$confirm(confirm_message, this.$t('admin.modal_confirm'))
        .then(() => createOrUpdate(params))
        .then(({ seq }) => this.$emit('submit', seq))
        .then(() => this.$toasted.success(success_message))
        .catch((e) => (e ? this.$toasted.error(e.message) : null))
        .finally(() => {
          this.loading = false
        })
    },
    handleAddDivearn() {
      this.instrument_divearns.push({
        instrument_seq: this.info.seq,
        divearn_asset_seq: undefined,
        divearn_amount: '0',
      })
    },
    async handleDeleteDivearn(idx) {
      this.loading = true
      try {
        if (this.instrument_divearns[idx].seq) {
          if (
            !(await this.$confirm(
              this.$t('admin.confirm_delete'),
              this.$t('admin.modal_confirm'),
              {
                type: 'warning',
              }
            ).catch(() => false))
          )
            return

          await deleteInstrumentDivearn(
            this.info.seq,
            this.instrument_divearns[idx].seq
          )
        }

        this.$notify({
          title: this.$t('toasted_msg.title'),
          message: this.$t('admin.msg_delete'),
          type: 'success',
        })
        this.instrument_divearns.splice(idx, 1)
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    handleSubmitDivearn(idx) {
      this.loading = true
      const params = this.instrument_divearns[idx]
      createInstrumentDivearn(params)
        .then(({ seq }) => fetchInstrumentDivearn(this.info.seq, seq))
        .then(({ divearn_info }) => {
          this.$notify({
            title: this.$t('toasted_msg.title'),
            message: params.seq
              ? this.$t('admin.msg_save_success')
              : this.$t('admin.msg_enroll_success'),
            type: 'success',
          })
          this.$set(this.instrument_divearns, idx, { ...divearn_info })
        })
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    date,
    fromPairs,
  },
}
</script>

<template>
  <el-tabs v-model="active_tab" :stretch="true">
    <el-tab-pane :label="$t('admin.default_info')" name="info">
      <form
        v-loading="loading"
        action="./"
        method="get"
        @submit.prevent="handleSubmit"
      >
        <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
          <colgroup>
            <col width="20%" />
            <col width="30%" />
            <col width="20%" />
            <col width="30%" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="row"
                ><div>{{ $t('finance_instrument_info.title') }}</div></th
              >
              <td colspan="3"
                ><LangItem v-if="!loading" v-model="params.title"></LangItem
              ></td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{
                  $t('finance_instrument_info.description_summary')
                }}</div></th
              >
              <td colspan="3"
                ><LangItem v-if="!loading" v-model="params.summary"></LangItem
              ></td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{
                  $t('finance_instrument_info.type_of_financial')
                }}</div></th
              >
              <td>
                <template v-if="params.seq">{{
                  fromPairs(constants.finance.instrument.instrument_type)[
                    params.instrument_type
                  ]
                }}</template>
                <select v-else v-model="params.instrument_type">
                  <option v-if="!params.seq" :value="null">{{
                    $t('finance_instrument_info.select_product_type')
                  }}</option>
                  <option
                    v-for="val in [
                      ...constants.finance.instrument.instrument_type,
                    ]"
                    :key="val[1]"
                    :value="val[0]"
                    >{{ val[1] }}</option
                  >
                </select>
              </td>
              <th scope="row"
                ><div>{{ $t('finance_instrument_info.application') }}</div></th
              >
              <td>
                <template v-if="params.seq">
                  {{ params.contract_form_title }}
                </template>
                <select
                  v-else
                  v-model="params.contract_form_seq"
                  :class="[$style.fullWidth]"
                >
                  <option
                    v-for="{ seq, title } in [
                      {
                        seq: null,
                        title: $t('finance_instrument_info.select'),
                      },
                      ...contract_forms,
                    ]"
                    :key="seq"
                    :value="seq"
                    >{{ title }}</option
                  >
                </select>
              </td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('finance_instrument_info.name') }}</div></th
              >
              <td>
                <template v-if="params.seq">{{ params.name }}</template>
                <input
                  v-else
                  v-model="params.name"
                  type="text"
                  :class="[$style.inputTypeText, $style.fullWidth]"
                />
              </td>
              <th scope="row"
                ><div>{{
                  $t('finance_instrument_info.executable_asset')
                }}</div></th
              >
              <td>
                <template v-if="params.seq && c_execute_asset_info">
                  {{ c_execute_asset_info.title }}
                </template>
                <select
                  v-else
                  v-model="params.execute_asset_seq"
                  :class="[$style.fullWidth]"
                >
                  <option
                    v-for="{ seq, title } in [
                      {
                        seq: null,
                        title: $t('finance_instrument_info.select_asset'),
                      },
                      ...assets,
                    ]"
                    :key="seq"
                    :value="seq"
                    >{{ title }}</option
                  >
                </select>
              </td>
            </tr>
            <tr>
              <th scope="row">
                <div>
                  {{ $t('finance_instrument_info.subscription_start_date') }}
                  <ElCheckbox
                    v-model="params.use_open_start"
                    :class="$style.fr"
                    false-label="N"
                    true-label="Y"
                    >{{ $t('finance_instrument_info.use') }}</ElCheckbox
                  >
                </div>
              </th>
              <td>
                <el-date-picker
                  v-if="params.use_open_start === 'Y'"
                  v-model="params.open_start"
                  type="datetime"
                  :placeholder="
                    $t('finance_instrument_info.msg_select_start_date')
                  "
                >
                </el-date-picker>
                <template v-else>
                  {{ $t('finance_instrument_info.disable_subscription_start') }}
                </template>
              </td>
              <th scope="row">
                <div>
                  {{ $t('finance_instrument_info.end_date') }}
                  <ElCheckbox
                    v-model="params.use_open_end"
                    :class="$style.fr"
                    false-label="N"
                    true-label="Y"
                    >{{ $t('finance_instrument_info.use') }}</ElCheckbox
                  >
                </div>
              </th>
              <td>
                <el-date-picker
                  v-if="params.use_open_end === 'Y'"
                  v-model="params.open_end"
                  type="datetime"
                  :placeholder="
                    $t('finance_instrument_info.msg_select_end_date')
                  "
                >
                </el-date-picker>
                <template v-else>
                  {{ $t('finance_instrument_info.disable_subscription_end') }}
                </template>
              </td>
            </tr>
            <tr>
              <th scope="row">
                <div>
                  {{ $t('finance_instrument_info.date_of_execution') }}
                </div>
              </th>
              <td>
                <el-date-picker
                  v-model="params.execute_start"
                  type="datetime"
                  :placeholder="
                    $t('finance_instrument_info.msg_select_start_date')
                  "
                >
                </el-date-picker>
              </td>
              <th scope="row">
                <div>
                  {{ $t('finance_instrument_info.execute_end') }}
                </div>
              </th>
              <td>
                <el-date-picker
                  v-model="params.execute_end"
                  type="datetime"
                  :placeholder="
                    $t('finance_instrument_info.msg_select_end_date.')
                  "
                >
                </el-date-picker>
              </td>
            </tr>
            <tr>
              <th scope="row">
                <div>
                  {{ $t('finance_instrument_info.settlement_date') }}
                </div>
              </th>
              <td>
                <el-date-picker
                  v-model="params.adjust_start"
                  type="datetime"
                  :placeholder="
                    $t('finance_instrument_info.msg_select_start_date')
                  "
                >
                </el-date-picker>
              </td>
              <th scope="row">
                <div>
                  {{ $t('finance_instrument_info.execution_amount_bundle') }}
                </div>
              </th>
              <td>
                <div v-if="c_execute_asset_info" :class="[]">
                  <input
                    v-model="params.execute_amount_step"
                    type="text"
                    :class="[$style.inputTypeText, $style.tRight, $style.w100]"
                  />
                  {{
                    $t('finance_instrument_info.join_and_run', [
                      c_execute_asset_info.code,
                    ])
                  }}
                </div>
                <div v-else :class="[$style.infoText]">
                  <p :class="[$style.warning]">{{
                    $t('finance_instrument_info.msg_execution_asset')
                  }}</p>
                </div>
              </td>
            </tr>
            <tr>
              <th scope="row">
                <div>
                  {{ $t('finance_instrument_info.minimum_amount') }}
                  <ElCheckbox
                    v-model="params.use_execute_amount_min"
                    :class="$style.fr"
                    false-label="N"
                    true-label="Y"
                    >{{ $t('finance_instrument_info.use') }}</ElCheckbox
                  >
                </div>
              </th>
              <td>
                <template v-if="params.use_execute_amount_min === 'Y'">
                  <div v-if="c_execute_asset_info" :class="[$style.fr]">
                    <input
                      v-model="params.execute_amount_min"
                      type="text"
                      :class="[
                        $style.inputTypeText,
                        $style.tRight,
                        $style.w120,
                      ]"
                    />
                    {{ c_execute_asset_info.code }}
                  </div>
                  <div v-else :class="[$style.infoText]">
                    <p :class="[$style.warning]">{{
                      $t('finance_instrument_info.msg_execution_asset')
                    }}</p>
                  </div>
                </template>
                <template v-else>
                  {{ $t('finance_instrument_info.disable_minimum') }}
                </template>
              </td>
              <th scope="row">
                <div>
                  {{ $t('finance_instrument_info.maximum_amount') }}
                  <ElCheckbox
                    v-model="params.use_execute_amount_max"
                    :class="$style.fr"
                    false-label="N"
                    true-label="Y"
                    >{{ $t('finance_instrument_info.use') }}</ElCheckbox
                  >
                </div>
              </th>
              <td>
                <template v-if="params.use_execute_amount_max === 'Y'">
                  <div v-if="c_execute_asset_info" :class="[$style.fr]">
                    <input
                      v-model="params.execute_amount_max"
                      type="text"
                      :class="[
                        $style.inputTypeText,
                        $style.tRight,
                        $style.w120,
                      ]"
                    />
                    {{ c_execute_asset_info.code }}
                  </div>
                  <div v-else :class="[$style.infoText]">
                    <p :class="[$style.warning]">{{
                      $t('finance_instrument_info.msg_execution_asset')
                    }}</p>
                  </div>
                </template>
                <template v-else>
                  {{ $t('finance_instrument_info.disable_maximum') }}
                </template>
              </td>
            </tr>
            <tr>
              <th colspan="4" :class="$style.button">
                <span :class="[$style.button, $style.black]"
                  ><input
                    type="submit"
                    :value="params.seq ? $t('admin.save') : $t('admin.enroll')"
                /></span>
              </th>
            </tr>
          </tbody>
        </table>
      </form>
    </el-tab-pane>
    <el-tab-pane
      :disabled="!info || !info.seq"
      :label="$t('finance_instrument_info.profit_for_dividends')"
      name="DIVEARN"
    >
      <table
        v-if="info && info.seq"
        cellspacing="0"
        cellpadding="0"
        :class="$style.colTable"
      >
        <thead>
          <tr>
            <th scope="col">
              <div>
                {{ $t('finance_instrument_info.dividend_profit_assets') }}
              </div>
            </th>
            <th scope="col">
              <div :style="[$style.nowrap]">
                {{ $t('finance_instrument_info.dividend_income') }}
              </div>
            </th>
            <th scope="col">
              <div :class="$style.tRight">
                <a
                  href="#"
                  :class="[$style.black, $style.button]"
                  @click="handleAddDivearn"
                  ><span>{{ $t('admin.add') }}</span></a
                >
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(instrument_divearn, idx) in instrument_divearns"
            :key="
              instrument_divearn.seq
                ? `${instrument_divearn.seq}`
                : `create-${idx}`
            "
          >
            <td :class="$style.wide">
              <select
                v-model="instrument_divearn.divearn_asset_seq"
                :disabled="instrument_divearn.seq"
              >
                <option :value="undefined">{{
                  $t('finance_instrument_info.choice')
                }}</option>
                <option v-for="val in assets" :key="val.seq" :value="val.seq">
                  {{ val.title }}
                </option>
              </select>
            </td>
            <td :class="[$style.tRight, $style.nowrap]">
              <template v-if="instrument_divearn.seq">
                {{ instrument_divearn.divearn_amount }}
              </template>
              <input
                v-else
                v-model="instrument_divearn.divearn_amount"
                type="text"
                :class="[$style.inputTypeText, $style.w120, $style.tRight]"
              />
            </td>
            <td :class="[$style.tRight, $style.nowrap]">
              <a
                v-if="!instrument_divearn.seq"
                href="#"
                :class="[$style.black, $style.button]"
                @click="handleSubmitDivearn(idx)"
                ><span>{{ $t('admin.enroll') }}</span></a
              >
            </td>
          </tr>
          <tr v-if="!instrument_divearns.length">
            <td :class="$style.tCenter" colspan="3">{{
              $t('finance_instrument_info.msg_no_benefit')
            }}</td>
          </tr>
        </tbody>
      </table>
    </el-tab-pane>
  </el-tabs>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';

.rowTable th,
.rowTable td,
.colTable th,
.colTable td {
  vertical-align: middle;
}

table div.infoText {
  margin-bottom: 0;
}
table div.infoText p.warning {
  margin: 0;
}
</style>
